import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  Trophy, Clock, AlertCircle, CheckCircle2, XCircle, ChevronLeft,
  ChevronRight, Loader2, FileText, Send, ArrowLeft, ListChecks,
  Award, Crown, BookOpenCheck, Sparkles, Zap, Lock, Download,
  ShieldCheck, Star, Flame, Target,
} from "lucide-react";
import { apiGet, apiPost } from "../lib/api";
import { registerDevice, buildFingerprintComponents } from "../lib/deviceFingerprint";
import { FADE_UP } from "../lib/motionVariants";
import {
  ExamIncidentBanner, pickRandomIncident,
} from "../components/ExamIncidentBanner";

// ===== bilingual copy =====
const UI = {
  it: {
    title: "Simulatore Reale · Esame di Riconoscimento",
    eyebrow: "Modo Esame Reale",
    intro_subtitle:
      "Riproduce le condizioni dell'esame ministeriale italiano: 5 moduli da 30 domande. Per superare devi ottenere almeno 18/30 in OGNI modulo. Nessuna compensazione.",
    rules_title: "Regole",
    rule_1: "5 moduli · 30 domande ciascuno · 150 totali",
    rule_2: "Soglia di superamento: 18/30 per modulo",
    rule_3: "Una sola insufficienza per modulo → esame non superato",
    rule_4: "Le risposte corrette restano nascoste fino alla consegna",
    rule_5: "Cronometro per modulo · 60 min ciascuno · auto-blocco al termine",
    cta_start: "Inizia il simulatore",
    cta_resume: "Riprendi simulatore in corso",
    cta_loading: "Preparo le domande…",
    history_title: "I tuoi tentativi",
    history_empty: "Non hai ancora effettuato il simulatore.",
    history_passed: "Superato",
    history_failed: "Non superato",
    history_in_progress: "In corso",
    history_view: "Rivedi",
    timer_label: "Tempo rimanente",
    nav_prev: "Precedente",
    nav_next: "Successiva",
    nav_finish: "Consegna esame",
    confirm_finish_title: "Sei sicuro di voler consegnare?",
    confirm_finish_desc: "Una volta consegnato non potrai modificare le risposte.",
    confirm_finish_unanswered:
      "Hai {n} domande senza risposta. Conteranno come errori.",
    confirm_finish_cta: "Sì, consegna l'esame",
    confirm_finish_cancel: "No, continua",
    progress_label: "Risposte",
    of: "di",
    module_label: "Modulo",
    question_label: "Domanda",
    summary_title_passed: "Esame superato!",
    summary_title_failed: "Esame non superato",
    summary_desc_passed:
      "Hai raggiunto la soglia in tutti i moduli. Ottimo lavoro.",
    summary_desc_failed:
      "Devi ottenere almeno 18/30 in OGNI modulo. Riprova quando sei pronto.",
    summary_total: "Punteggio globale",
    summary_per_module: "Risultati per modulo",
    summary_passed_pill: "Superato",
    summary_failed_pill: "Insufficiente",
    summary_review_correct: "Risposta corretta",
    summary_review_your: "La tua risposta",
    summary_back_home: "Torna all'Academia",
    summary_new_attempt: "Nuovo tentativo",
    summary_review_section: "Rivedi le tue risposte",
    expired_banner: "Il tempo è scaduto. L'esame è stato consegnato automaticamente.",
    in_progress_active: "Hai un esame in corso",
    error_generic: "Qualcosa è andato storto. Riprova.",
    answer_unanswered: "Senza risposta",
    threshold_min: "minimo",
    // Phase 2 monetization
    credits_eyebrow: "Tentativi disponibili",
    credits_free: "{n} gratuiti",
    credits_paid: "{n} acquistati",
    credits_consumed_free: "Tentativo gratuito utilizzato · {n} rimasti",
    credits_consumed_paid: "Tentativo a pagamento utilizzato · {n} rimasti",
    paywall_title: "Hai esaurito i tentativi del simulatore",
    paywall_desc:
      "Sblocca nuovi tentativi con un pacchetto a scelta. Pagamento sicuro Stripe · accesso immediato.",
    paywall_recommended: "Consigliato",
    pack_10_title: "5 tentativi",
    pack_10_price: "€49,90",
    pack_10_cta: "Sblocca 5 tentativi",
    pack_10_note: "€9,98 a tentativo",
    pack_20_title: "7 tentativi",
    pack_20_price: "€79,90",
    pack_20_cta: "Sblocca 7 tentativi",
    pack_20_note: "€11,41 a tentativo · sblocca tutte le difficoltà",
    pack_unavailable: "Non disponibile",
    pack_after_purchase: "Dopo il pagamento il credito si aggiornerà automaticamente.",
    // Phase 5 — per-module timer + difficulty selector
    difficulty_label: "Difficoltà",
    difficulty_easy: "Facile",
    difficulty_medium: "Medio",
    difficulty_hard: "Difficile",
    difficulty_locked_free: "Riservato a chi ha acquistato un pacchetto",
    difficulty_free_hint: "I tentativi gratuiti partono solo in difficoltà Facile. Sblocca Medio e Difficile con un pacchetto.",
    module_timer_label: "Tempo modulo",
    module_warning_title: "Il professore sta raccogliendo i fogli",
    module_warning_body: "Sbrigati! Ha già iniziato a chiedere i fogli ai primi banchi. Hai meno di 10 minuti per finire questo modulo.",
    module_warning_ack: "Ho capito, continuo",
    // ─── Phase 8: progressive psychological pressure ───
    tension_calm: "In corso",
    tension_watch: "Tempo limitato",
    tension_critical: "Ultimi minuti",
    tension_imminent: "Tempo in scadenza",
    transition_eyebrow: "La commissione",
    transition_title: "Si passa al prossimo modulo",
    transition_subtitle: "{label} · 60 minuti",
    transition_aria: "Transizione al modulo successivo",
    timeup_title: "Tempo scaduto",
    timeup_subtitle: "Il modulo è stato bloccato.",
    timeup_next: "Si passa al modulo successivo…",
    timeup_last: "Esame in consegna…",
    commission_waiting: "La commissione sta aspettando…",
    module_locked_badge: "Modulo bloccato",
    module_advance_cta: "Conferma modulo e passa al successivo",
    module_advance_last_cta: "Conferma ultimo modulo e consegna",
    module_advance_confirm_title: "Confermare il modulo?",
    module_advance_confirm_desc: "Una volta confermato non potrai più modificare le risposte di questo modulo.",
    module_advance_confirm_yes: "Sì, blocca modulo",
    module_advance_confirm_no: "No, continuo",
    module_expired_toast: "Tempo scaduto. Modulo bloccato — passo al successivo.",
    // ─── Final-module completion modal (Phase 6.3) ──────────────
    final_modal_title: "🎓 Complimenti! Hai raggiunto la fine",
    final_modal_subtitle: "Stai per chiudere l'ultimo modulo del simulatore reale.",
    final_modal_provisional: "Risposte completate",
    final_modal_score_great: "Eccellente lavoro!",
    final_modal_score_ok: "Buon risultato.",
    final_modal_score_low: "Hai bisogno di più allenamento.",
    final_modal_explain_great: "Hai dimostrato di aver assimilato i contenuti chiave. Il prossimo passo è ripetere il simulatore in difficoltà Media o Difficile per consolidare il risultato. La sicurezza nelle risposte è la differenza tra promosso e bocciato all'esame ministeriale.",
    final_modal_explain_low: "Non scoraggiarti. Il simulatore funziona come un allenamento: ogni tentativo migliora la velocità di riconoscimento delle domande tipo. Riprova con un pacchetto aggiuntivo e concentrati sui moduli con punteggio più basso.",
    final_modal_buy_attempts_cta: "Sblocca più tentativi (€49,90)",
    final_modal_amazon_title: "📕 Il libro del prof. Terzano",
    final_modal_amazon_desc: "«Teorico-Pratica per superare l'esame di riconoscimento in Medicina» — disponibile su Amazon.",
    final_modal_amazon_cta: "Acquista su Amazon",
    final_modal_aims_title: "📋 Pacchetto AIMS",
    final_modal_aims_desc: "Le simulazioni AIMS aggiornate per esercitarti a casa — solo €9,99.",
    final_modal_aims_cta: "Scarica AIMS — €9,99",
    final_modal_aims_unavailable: "Disponibile a breve",
    final_modal_confirm_cta: "Conferma e consegna l'esame",
    final_modal_cancel: "Annulla",
    // Phase 3 — certificate
    cert_eyebrow: "Certificato Premium",
    cert_title_card: "Hai conquistato il tuo certificato",
    cert_desc:
      "Scarica il PDF ufficiale del Simulatore Reale. Include sigillo Premium, ID univoco e QR di verifica pubblica.",
    cert_download: "Scarica certificato PDF",
    cert_downloading: "Generazione…",
    cert_history_badge: "Certificato disponibile",
    cert_history_view: "PDF",
    // Achievement badges (IT)
    badge_first_pass_title: "Primo Successo",
    badge_first_pass_desc: "Il tuo primo simulatore superato.",
    badge_perfect_title: "Senza Errori",
    badge_perfect_desc: "150 risposte corrette su 150. Impeccabile.",
    badge_triplete_title: "Triplete",
    badge_triplete_desc: "Tre simulatori superati di fila.",
    badge_section: "Riconoscimenti sbloccati",
    // Phase 7 — Intro mode + preview funnel + device gate
    intro_mode_badge: "Modo introduttivo",
    intro_mode_eyebrow: "Allenamento introduttivo",
    intro_mode_title: "Allenati al ritmo dell'esame italiano",
    intro_mode_subtitle:
      "Un modulo · 10 domande · 15 minuti. Una piccola prova per scoprire il livello iniziale dell'esame ministeriale.",
    intro_mode_cta_start: "Inizia la prova introduttiva",
    intro_mode_rule_1: "1 modulo · 10 domande · 15 minuti",
    intro_mode_rule_2: "Difficoltà introduttiva (livello facile)",
    intro_mode_rule_3: "Le risposte corrette restano nascoste fino alla consegna",
    // After intro completion → motivational screen
    intro_done_eyebrow: "Modo introduttivo completato",
    intro_done_title: "Hai completato il modo introduttivo",
    intro_done_subtitle:
      "Ora inizia la vera simulazione dell'esame italiano.",
    intro_done_message:
      "Il livello reale richiede velocità, precisione, ragionamento clinico e capacità di gestire la pressione. Quello che hai appena fatto è solo l'inizio.",
    intro_done_score: "Hai risposto correttamente a {n} su {total}",
    intro_done_cta_real: "🔥 Accedi all'esame reale",
    intro_done_cta_loading: "Preparo la simulazione reale…",
    intro_done_cta_back: "Torna all'Academia",
    // Preview (3 hard questions) → paywall after lock
    preview_mode_eyebrow: "Esame reale · anteprima",
    preview_mode_title: "Anteprima dell'esame reale",
    preview_mode_subtitle:
      "3 domande di livello reale, senza cronometro. Quando finisci queste 3, sblocchi i pacchetti completi.",
    preview_mode_progress: "Anteprima · {current} di {total}",
    preview_mode_locked_eyebrow: "Anteprima completata",
    preview_mode_locked_title: "Hai sbloccato il livello reale dell'esame",
    preview_mode_locked_subtitle: "Continua con difficoltà media e difficile, simulazione completa, cronometro reale, moduli ufficiali e l'esperienza orale avanzata.",
    preview_mode_locked_features: [
      "Difficoltà media e difficile",
      "Simulazione completa (5 moduli · 150 domande)",
      "Cronometro reale per modulo",
      "Moduli ufficiali del programma ministeriale",
      "Esperienza orale avanzata con avatar e voce",
    ],
    // Device-fingerprint gate
    device_blocked_eyebrow: "Anteprima introduttiva già utilizzata",
    device_blocked_title: "Questo dispositivo ha già fatto la prova introduttiva",
    device_blocked_desc:
      "Per garantire l'integrità della preparazione, ogni dispositivo può fare la prova introduttiva una sola volta. Per continuare scegli un pacchetto reale qui sotto.",
    device_blocked_help: "Condividi il PC con un collega? Scrivici a info@esamericonoscimento.it.",
  },
  es: {
    title: "Simulador Real · Examen de Reconocimiento",
    eyebrow: "Modo Examen Real",
    intro_subtitle:
      "Reproduce las condiciones del examen ministerial italiano: 5 módulos de 30 preguntas. Para aprobar necesitas mínimo 18/30 en CADA módulo. Sin compensación.",
    rules_title: "Reglas",
    rule_1: "5 módulos · 30 preguntas cada uno · 150 totales",
    rule_2: "Umbral de aprobación: 18/30 por módulo",
    rule_3: "Un solo módulo reprobado → examen no aprobado",
    rule_4: "Las respuestas correctas permanecen ocultas hasta entregar",
    rule_5: "Cronómetro por módulo · 60 min cada uno · auto-bloqueo al final",
    cta_start: "Iniciar el simulador",
    cta_resume: "Retomar simulador en curso",
    cta_loading: "Preparando las preguntas…",
    history_title: "Tus intentos",
    history_empty: "Aún no has realizado el simulador.",
    history_passed: "Aprobado",
    history_failed: "No aprobado",
    history_in_progress: "En curso",
    history_view: "Revisar",
    timer_label: "Tiempo restante",
    nav_prev: "Anterior",
    nav_next: "Siguiente",
    nav_finish: "Entregar examen",
    confirm_finish_title: "¿Estás seguro de entregar?",
    confirm_finish_desc: "Una vez entregado no podrás cambiar respuestas.",
    confirm_finish_unanswered:
      "Tienes {n} preguntas sin responder. Contarán como erradas.",
    confirm_finish_cta: "Sí, entregar el examen",
    confirm_finish_cancel: "No, continuar",
    progress_label: "Respuestas",
    of: "de",
    module_label: "Módulo",
    question_label: "Pregunta",
    summary_title_passed: "¡Examen aprobado!",
    summary_title_failed: "Examen no aprobado",
    summary_desc_passed:
      "Alcanzaste el umbral en todos los módulos. Excelente trabajo.",
    summary_desc_failed:
      "Necesitas mínimo 18/30 en CADA módulo. Reintenta cuando estés listo.",
    summary_total: "Puntuación global",
    summary_per_module: "Resultados por módulo",
    summary_passed_pill: "Aprobado",
    summary_failed_pill: "Insuficiente",
    summary_review_correct: "Respuesta correcta",
    summary_review_your: "Tu respuesta",
    summary_back_home: "Volver a la Academia",
    summary_new_attempt: "Nuevo intento",
    summary_review_section: "Revisa tus respuestas",
    expired_banner: "El tiempo expiró. El examen fue entregado automáticamente.",
    in_progress_active: "Tienes un examen en curso",
    error_generic: "Algo salió mal. Inténtalo de nuevo.",
    answer_unanswered: "Sin respuesta",
    threshold_min: "mínimo",
    // Phase 2 monetization
    credits_eyebrow: "Intentos disponibles",
    credits_free: "{n} gratuitos",
    credits_paid: "{n} comprados",
    credits_consumed_free: "Intento gratuito utilizado · {n} restantes",
    credits_consumed_paid: "Intento de pago utilizado · {n} restantes",
    paywall_title: "Has agotado los intentos del simulador",
    paywall_desc:
      "Desbloqueá nuevos intentos con el pack que prefieras. Pago seguro Stripe · acceso inmediato.",
    paywall_recommended: "Recomendado",
    pack_10_title: "5 intentos",
    pack_10_price: "€49,90",
    pack_10_cta: "Desbloquear 5 intentos",
    pack_10_note: "€9,98 por intento",
    pack_20_title: "7 intentos",
    pack_20_price: "€79,90",
    pack_20_cta: "Desbloquear 7 intentos",
    pack_20_note: "€11,41 por intento · desbloquea todas las dificultades",
    pack_unavailable: "No disponible",
    pack_after_purchase: "Tras el pago el crédito se actualizará automáticamente.",
    // Phase 5 — per-module timer + difficulty selector
    difficulty_label: "Dificultad",
    difficulty_easy: "Fácil",
    difficulty_medium: "Medio",
    difficulty_hard: "Difícil",
    difficulty_locked_free: "Reservado a quien compró un paquete",
    difficulty_free_hint: "Los intentos gratuitos solo arrancan en dificultad Fácil. Desbloqueá Medio y Difícil con un pack.",
    module_timer_label: "Tiempo del módulo",
    module_warning_title: "El profesor está pidiendo las hojas",
    module_warning_body: "¡Apurate! Ya empezó a recoger las hojas de los primeros bancos. Tenés menos de 10 minutos para cerrar este módulo.",
    module_warning_ack: "Entendido, continúo",
    // ─── Phase 8: progressive psychological pressure (mirror) ───
    tension_calm: "En curso",
    tension_watch: "Tiempo limitado",
    tension_critical: "Últimos minutos",
    tension_imminent: "Tiempo agotándose",
    transition_eyebrow: "La comisión",
    transition_title: "Pasamos al próximo módulo",
    transition_subtitle: "{label} · 60 minutos",
    transition_aria: "Transición al módulo siguiente",
    timeup_title: "Tiempo agotado",
    timeup_subtitle: "El módulo ha sido bloqueado.",
    timeup_next: "Pasando al siguiente módulo…",
    timeup_last: "Entregando el examen…",
    commission_waiting: "La comisión está esperando…",
    module_locked_badge: "Módulo bloqueado",
    module_advance_cta: "Confirmar módulo y pasar al siguiente",
    module_advance_last_cta: "Confirmar último módulo y entregar",
    module_advance_confirm_title: "¿Confirmar el módulo?",
    module_advance_confirm_desc: "Una vez confirmado no podrás modificar las respuestas de este módulo.",
    module_advance_confirm_yes: "Sí, bloquear módulo",
    module_advance_confirm_no: "No, continúo",
    module_expired_toast: "Tiempo agotado. Módulo bloqueado — paso al siguiente.",
    // Phase 3 — certificate
    cert_eyebrow: "Certificado Premium",
    cert_title_card: "Conseguiste tu certificado",
    cert_desc:
      "Descargá el PDF oficial del Simulador Real. Incluye sello Premium, ID único y QR de verificación pública.",
    cert_download: "Descargar certificado PDF",
    cert_downloading: "Generando…",
    cert_history_badge: "Certificado disponible",
    cert_history_view: "PDF",
    // Achievement badges (ES)
    badge_first_pass_title: "Primera Aprobación",
    badge_first_pass_desc: "Tu primer simulador aprobado.",
    badge_perfect_title: "Sin Errores",
    badge_perfect_desc: "150 respuestas correctas sobre 150. Impecable.",
    badge_triplete_title: "Triplete",
    badge_triplete_desc: "Tres simuladores aprobados consecutivos.",
    badge_section: "Reconocimientos desbloqueados",
    // Phase 7 — Intro mode + preview funnel + device gate (ES mirror)
    intro_mode_badge: "Modo introductorio",
    intro_mode_eyebrow: "Entrenamiento introductorio",
    intro_mode_title: "Entrenate al ritmo del examen italiano",
    intro_mode_subtitle:
      "Un módulo · 10 preguntas · 15 minutos. Una prueba rápida para descubrir el nivel inicial del examen ministerial.",
    intro_mode_cta_start: "Iniciar la prueba introductoria",
    intro_mode_rule_1: "1 módulo · 10 preguntas · 15 minutos",
    intro_mode_rule_2: "Dificultad introductoria (nivel fácil)",
    intro_mode_rule_3: "Las respuestas correctas permanecen ocultas hasta entregar",
    intro_done_eyebrow: "Modo introductorio completado",
    intro_done_title: "Has completado el modo introductorio",
    intro_done_subtitle: "Ahora comienza la verdadera simulación del examen italiano.",
    intro_done_message:
      "El nivel real exige velocidad, precisión, razonamiento clínico y manejo de la presión. Lo que acabás de hacer es solo el comienzo.",
    intro_done_score: "Respondiste correctamente {n} de {total}",
    intro_done_cta_real: "🔥 Acceder al examen real",
    intro_done_cta_loading: "Preparo la simulación real…",
    intro_done_cta_back: "Volver a la Academia",
    preview_mode_eyebrow: "Examen real · vista previa",
    preview_mode_title: "Vista previa del examen real",
    preview_mode_subtitle:
      "3 preguntas de nivel real, sin cronómetro. Al terminar estas 3 desbloqueás los paquetes completos.",
    preview_mode_progress: "Vista previa · {current} de {total}",
    preview_mode_locked_eyebrow: "Vista previa completada",
    preview_mode_locked_title: "Has desbloqueado el nivel real del examen",
    preview_mode_locked_subtitle: "Continúa con dificultad media y difícil, simulación completa, cronómetro real, módulos oficiales y la experiencia oral avanzada.",
    preview_mode_locked_features: [
      "Dificultad media y difícil",
      "Simulación completa (5 módulos · 150 preguntas)",
      "Cronómetro real por módulo",
      "Módulos oficiales del programa ministerial",
      "Experiencia oral avanzada con avatar y voz",
    ],
    device_blocked_eyebrow: "Prueba introductoria ya utilizada",
    device_blocked_title: "Este dispositivo ya hizo la prueba introductoria",
    device_blocked_desc:
      "Para preservar la integridad de la preparación, cada dispositivo puede hacer la prueba introductoria una sola vez. Para continuar elegí un paquete real abajo.",
    device_blocked_help: "¿Compartís la PC con un colega? Escribinos a info@esamericonoscimento.it.",
  },
};

const slotLabel = (s, locale) => (locale === "es" ? s.label_es : s.label_it);

const formatHMS = (totalSec) => {
  if (totalSec < 0) totalSec = 0;
  const h = Math.floor(totalSec / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const pad = (n) => String(n).padStart(2, "0");
  return h > 0 ? `${pad(h)}:${pad(m)}:${pad(s)}` : `${pad(m)}:${pad(s)}`;
};

const formatDate = (iso, locale) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString(locale === "es" ? "es-ES" : "it-IT", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso.slice(0, 16); }
};

// ===== Credits chip + Paywall =====
function CreditsChip({ t, credits }) {
  if (!credits) return null;
  const { free_left, paid_left } = credits;
  const items = [];
  if (free_left > 0) items.push({ label: t.credits_free.replace("{n}", free_left), bg: "#E8F5EE", fg: "#1A6B3A", icon: Sparkles });
  if (paid_left > 0) items.push({ label: t.credits_paid.replace("{n}", paid_left), bg: "#FFF8E7", fg: "#7A4F00", icon: Zap });
  if (items.length === 0) {
    items.push({ label: "0", bg: "#FCE9EE", fg: "#9E2A48", icon: Lock });
  }
  return (
    <div className="inline-flex items-center gap-2 flex-wrap" data-testid="vip-exam-credits-chip">
      <span className="text-[10px] uppercase tracking-[0.22em] text-brand-secondary">
        {t.credits_eyebrow}
      </span>
      {items.map((it, i) => {
        const Icon = it.icon;
        return (
          <span key={i}
                className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium"
                style={{ background: it.bg, color: it.fg }}>
            <Icon className="h-3 w-3" /> {it.label}
          </span>
        );
      })}
    </div>
  );
}

function PaywallView({ t, packs }) {
  const p10 = packs?.exam_pack_10 || {};
  const p20 = packs?.exam_pack_20 || {};
  return (
    <motion.div data-testid="vip-exam-paywall"
                initial="hidden" animate="show" variants={FADE_UP}
                className="rounded-2xl border-2 p-6 sm:p-8 mb-6"
                style={{ borderColor: "#E25C7E", background: "#FFF5F8" }}>
      <div className="flex items-start gap-3 mb-5 flex-wrap">
        <div className="h-12 w-12 rounded-2xl grid place-items-center shrink-0"
             style={{ background: "#FCEAEE" }}>
          <Lock className="h-6 w-6" style={{ color: "#9E2A48" }} />
        </div>
        <div className="flex-1 min-w-0">
          <h2 className="font-serif text-2xl sm:text-3xl leading-tight" style={{ color: "#9E2A48" }}>
            {t.paywall_title}
          </h2>
          <p className="text-brand-primary mt-2 leading-relaxed">
            {t.paywall_desc}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-4">
        {/* Pack 10 */}
        <PackCard pack={p10} t={t}
                  title={t.pack_10_title} price={t.pack_10_price}
                  cta={t.pack_10_cta} note={t.pack_10_note}
                  testId="vip-exam-pack-10" />
        {/* Pack 20 (recommended — gold border) */}
        <PackCard pack={p20} t={t}
                  title={t.pack_20_title} price={t.pack_20_price}
                  cta={t.pack_20_cta} note={t.pack_20_note}
                  recommended testId="vip-exam-pack-20" />
      </div>

      <p className="text-xs text-brand-secondary mt-4 leading-relaxed">
        {t.pack_after_purchase}
      </p>
    </motion.div>
  );
}

function PackCard({ pack, t, title, price, cta, note, recommended, testId }) {
  const available = !!pack?.url;
  const borderColor = recommended ? "#D4AF37" : "#E5DCEF";
  return (
    <div data-testid={testId}
         className="rounded-xl border-2 bg-white p-5 flex flex-col"
         style={{ borderColor }}>
      {recommended && (
        <span className="inline-flex self-start items-center gap-1 mb-2 px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wider"
              style={{ background: "#FFF1C2", color: "#7A4F00" }}>
          <Sparkles className="h-3 w-3" /> {t.paywall_recommended}
        </span>
      )}
      <h3 className="font-serif text-2xl text-brand-primary">{title}</h3>
      <p className="font-serif text-3xl text-brand-primary mt-1">{price}</p>
      <p className="text-xs text-brand-secondary mt-1">{note}</p>
      <div className="mt-auto pt-4">
        {available ? (
          <a href={pack.url} target="_blank" rel="noopener noreferrer"
             data-testid={`${testId}-cta`}
             className="block text-center w-full px-4 py-3 rounded-xl font-semibold text-white transition-colors"
             style={{ background: recommended ? "#5A3FC0" : "#112A1E" }}>
            {cta}
          </a>
        ) : (
          <button disabled
                  data-testid={`${testId}-cta`}
                  className="w-full px-4 py-3 rounded-xl font-semibold text-white opacity-50 cursor-not-allowed"
                  style={{ background: "#9CA3AF" }}>
            {t.pack_unavailable}
          </button>
        )}
      </div>
    </div>
  );
}

// ===== Intro / hub =====
function IntroView({ t, language, onStart, starting, hasInProgress, history,
                    credits, packs, difficulty, setDifficulty, canPickDifficulty,
                    freeMode = false }) {
  const noCredits = !hasInProgress
    && credits
    && credits.total_left === 0;
  // Locked difficulty click → straight to Stripe checkout (the lock IS
  // the CTA). Prefer pack_20 (recommended) over pack_10, fall back to
  // whichever has a configured URL.
  const openWrittenPack = () => {
    const url = packs?.exam_pack_20?.url || packs?.exam_pack_10?.url;
    if (url) window.open(url, "_blank", "noopener,noreferrer");
    else toast.info(t.pack_unavailable);
  };
  const handleDifficultyClick = (key, isLocked) => {
    if (!isLocked) { setDifficulty(key); return; }
    openWrittenPack();
  };
  return (
    <motion.section data-testid="vip-exam-intro"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-4xl mx-auto">
      <p className="text-[11px] uppercase tracking-[0.22em] text-brand-secondary mb-3 inline-flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5" /> {freeMode ? t.intro_mode_eyebrow : t.eyebrow}
      </p>
      <h1 className="font-serif text-4xl sm:text-5xl text-brand-primary leading-tight mb-3">
        {freeMode ? t.intro_mode_title : t.title}
      </h1>
      <p className="text-brand-secondary text-lg leading-relaxed mb-4">
        {freeMode ? t.intro_mode_subtitle : t.intro_subtitle}
      </p>

      {credits ? <div className="mb-5"><CreditsChip t={t} credits={credits} /></div> : null}

      {noCredits ? (
        <PaywallView t={t} packs={packs} />
      ) : null}

      <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8 mb-6">
        <h2 className="font-serif text-2xl text-brand-primary mb-4 flex items-center gap-2">
          <ListChecks className="h-5 w-5 text-brand-green" /> {t.rules_title}
        </h2>
        <ul className="space-y-2.5" data-testid="vip-exam-rules">
          {(freeMode
            ? [t.intro_mode_rule_1, t.intro_mode_rule_2, t.intro_mode_rule_3]
            : [t.rule_1, t.rule_2, t.rule_3, t.rule_4, t.rule_5]
          ).map((r, i) => (
            <li key={i} className="flex items-start gap-3 text-brand-primary">
              <span className="mt-1 shrink-0 h-5 w-5 rounded-full bg-brand-green/15 grid place-items-center">
                <CheckCircle2 className="h-3 w-3 text-brand-green" />
              </span>
              <span>{r}</span>
            </li>
          ))}
        </ul>

        {/* Difficulty selector — only meaningful when starting a fresh
            attempt (resuming inherits the prior difficulty). */}
        {!hasInProgress && (
          <div className="mt-6 pt-5 border-t border-brand-border">
            <p className="text-sm font-semibold text-brand-primary mb-3 inline-flex items-center gap-2">
              <Target className="h-4 w-4 text-brand-secondary" /> {t.difficulty_label}
            </p>
            <div className="flex flex-wrap gap-2" data-testid="vip-exam-difficulty">
              {[
                { key: "easy",   label: t.difficulty_easy,   alwaysAllowed: true  },
                { key: "medium", label: t.difficulty_medium, alwaysAllowed: false },
                { key: "hard",   label: t.difficulty_hard,   alwaysAllowed: false },
              ].map((d) => {
                const locked = !d.alwaysAllowed && !canPickDifficulty;
                const active = difficulty === d.key;
                return (
                  <button
                    key={d.key}
                    onClick={() => handleDifficultyClick(d.key, locked)}
                    data-testid={`vip-exam-difficulty-${d.key}`}
                    className={[
                      "px-4 py-2 rounded-full text-sm font-medium border transition-all inline-flex items-center gap-1.5",
                      active
                        ? "bg-brand-primary text-white border-brand-primary"
                        : locked
                          ? "bg-amber-50 text-amber-700 border-amber-200 hover:bg-amber-100 cursor-pointer"
                          : "bg-white text-brand-primary border-brand-border hover:border-brand-primary",
                    ].join(" ")}
                  >
                    {locked ? <Lock className="h-3.5 w-3.5" /> : null}
                    {d.label}
                    {locked ? <span className="text-[10px] ml-1 opacity-80">€49,90</span> : null}
                  </button>
                );
              })}
            </div>
            {!canPickDifficulty && (
              <p className="text-xs text-brand-secondary mt-2"
                 data-testid="vip-exam-difficulty-hint">
                {t.difficulty_free_hint}
              </p>
            )}
          </div>
        )}
      </div>

      <button data-testid="vip-exam-start-btn" onClick={onStart}
              disabled={starting || noCredits}
              className="inline-flex items-center justify-center gap-2 px-7 py-4 rounded-xl bg-brand-primary text-white font-semibold shadow-premium-lg hover:bg-brand-green transition-colors w-full sm:w-auto disabled:opacity-50 disabled:cursor-not-allowed">
        {starting
          ? <><Loader2 className="h-5 w-5 animate-spin" /> {t.cta_loading}</>
          : hasInProgress
            ? <><BookOpenCheck className="h-5 w-5" /> {t.cta_resume}</>
            : <><Trophy className="h-5 w-5" /> {freeMode ? t.intro_mode_cta_start : t.cta_start}</>}
      </button>

      <div className="mt-10">
        <h2 className="font-serif text-2xl text-brand-primary mb-3 flex items-center gap-2">
          <Award className="h-5 w-5 text-brand-secondary" /> {t.history_title}
        </h2>
        {history.length === 0 ? (
          <p className="text-brand-secondary" data-testid="vip-exam-history-empty">
            {t.history_empty}
          </p>
        ) : (
          <div className="space-y-2" data-testid="vip-exam-history-list">
            {history.map((a) => {
              const isInProg = a.status === "in_progress";
              const passed = a.passed === true;
              const pill = isInProg
                ? { txt: t.history_in_progress, bg: "#EDE7FF", fg: "#5A3FC0" }
                : passed
                  ? { txt: t.history_passed, bg: "#E8F5EE", fg: "#1A6B3A" }
                  : { txt: t.history_failed, bg: "#FCE9EE", fg: "#9E2A48" };
              const score = a.result
                ? `${a.result.total_correct}/${a.result.total_questions}`
                : "—";
              return (
                <Link key={a.attempt_id} to={`/vip/exam/${a.attempt_id}`}
                      data-testid={`vip-exam-history-${a.attempt_id}`}
                      className="block rounded-xl border border-brand-border bg-white px-4 py-3 hover:border-brand-primary transition-colors">
                  <div className="flex items-center gap-3 flex-wrap">
                    <span className="text-xs px-2.5 py-1 rounded-full font-medium"
                          style={{ background: pill.bg, color: pill.fg }}>
                      {pill.txt}
                    </span>
                    <span className="text-sm text-brand-primary font-medium">{score}</span>
                    <span className="text-xs text-brand-secondary">
                      {formatDate(a.started_at, language)}
                    </span>
                    {a.certificate_id ? (
                      <span className="text-xs px-2 py-0.5 rounded-full font-medium inline-flex items-center gap-1"
                            style={{ background: "#F4E9CC", color: "#7A4F00" }}
                            data-testid={`vip-exam-history-cert-${a.attempt_id}`}>
                        <Award className="h-3 w-3" /> {t.cert_history_view}
                      </span>
                    ) : null}
                    <span className="ml-auto text-xs text-brand-primary inline-flex items-center gap-1">
                      {t.history_view} <ChevronRight className="h-3.5 w-3.5" />
                    </span>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </motion.section>
  );
}

// ===== Runner: in-progress exam (per-module pacing) =====
//
// ─── Phase 8 — Progressive psychological pressure ────────────────────────
// `tensionLevel(secondsLeft)` returns a sober 4-stage pressure model:
//   calm    (>20 min) · neutral amber, no pulse
//   watch   (10-20 min) · amber chip, no pulse
//   critical (3-10 min) · rose chip + soft pulse on timer only
//   imminent (<3 min)   · deep red chip + steady pulse + red header rim
// This replaces the previous binary "isCritical at 10 min" so the visual
// load grows gradually, mirroring how the real ministerial countdown
// feels: calm, then watch, then critical, then close-to-zero. NO sound,
// NO emoji, NO bouncing — sober, médical, premium.
function tensionLevel(secondsLeft) {
  if (secondsLeft > 1200) return { tone: "calm",     pulse: false, urgent: false };
  if (secondsLeft > 600)  return { tone: "watch",    pulse: false, urgent: false };
  if (secondsLeft > 180)  return { tone: "critical", pulse: true,  urgent: true  };
  return                    { tone: "imminent", pulse: true,  urgent: true  };
}

// Color palette per tension tone (kept inline to avoid a separate CSS file).
const TENSION_PALETTE = {
  calm: {
    chipBg: "rgba(255, 248, 231, 0.85)",
    chipFg: "#7A4F00",
    chipRing: "rgba(122, 79, 0, 0.2)",
    headerBg: "rgba(251, 248, 242, 0.78)",
    headerRim: "rgba(229, 220, 239, 0.6)",
    barFill: "linear-gradient(90deg, #5A3FC0 0%, #B45309 100%)",
  },
  watch: {
    chipBg: "rgba(255, 244, 214, 0.95)",
    chipFg: "#7A4F00",
    chipRing: "rgba(122, 79, 0, 0.35)",
    headerBg: "rgba(255, 251, 240, 0.92)",
    headerRim: "rgba(180, 83, 9, 0.32)",
    barFill: "linear-gradient(90deg, #B45309 0%, #C2410C 100%)",
  },
  critical: {
    chipBg: "rgba(252, 233, 238, 0.95)",
    chipFg: "#9E2A48",
    chipRing: "rgba(158, 42, 72, 0.45)",
    headerBg: "rgba(255, 240, 244, 0.95)",
    headerRim: "rgba(158, 42, 72, 0.45)",
    barFill: "linear-gradient(90deg, #DC2626 0%, #9E2A48 100%)",
  },
  imminent: {
    chipBg: "rgba(254, 215, 222, 0.98)",
    chipFg: "#7A1A36",
    chipRing: "rgba(122, 26, 54, 0.6)",
    headerBg: "rgba(254, 226, 232, 0.96)",
    headerRim: "rgba(122, 26, 54, 0.55)",
    barFill: "linear-gradient(90deg, #B91C1C 0%, #7A1A36 100%)",
  },
};

// Module transition overlay — shown for 2.4s when the student advances
// from one module to the next. Serious, ministerial: black backdrop,
// Italian flag bar, sober copy. NO animations beyond opacity fades.
function ModuleTransitionOverlay({ t, nextSlotIndex, totalSlots, slotLabel: label }) {
  return (
    <motion.div
      data-testid="vip-exam-module-transition"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.35 }}
      role="status"
      aria-label={t.transition_aria}
      className="fixed inset-0 z-[60] grid place-items-center backdrop-blur-md"
      style={{ background: "rgba(8, 6, 14, 0.78)" }}
    >
      <div className="w-full max-w-md mx-auto px-6 text-center text-white">
        <div className="h-[3px] w-full flex mb-8 rounded-full overflow-hidden opacity-90" aria-hidden="true">
          <div className="flex-1" style={{ background: "#008C45" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#CD212A" }} />
        </div>
        <p className="text-[11px] uppercase tracking-[0.28em] opacity-70 mb-3">
          {t.transition_eyebrow}
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl mb-4 leading-tight">
          {t.transition_title}
        </h2>
        <p className="text-sm opacity-85 mb-1 tabular-nums">
          {t.module_label} {nextSlotIndex + 1}/{totalSlots}
        </p>
        <p className="text-base opacity-95">
          {t.transition_subtitle.replace("{label}", label)}
        </p>
      </div>
    </motion.div>
  );
}

// Auto-lock overlay — shown for 1.6s when the per-module timer hits 0.
// Replaces the silent toast with a brief, dignified screen so the
// student understands the system locked their module (not a bug).
function TimeUpOverlay({ t, isLast }) {
  return (
    <motion.div
      data-testid="vip-exam-timeup-overlay"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.3 }}
      role="alert"
      className="fixed inset-0 z-[60] grid place-items-center backdrop-blur-md"
      style={{ background: "rgba(14, 6, 8, 0.82)" }}
    >
      <div className="w-full max-w-sm mx-auto px-6 text-center text-white">
        <div
          className="h-[3px] w-full mb-7 rounded-full"
          style={{ background: "linear-gradient(90deg,#7A1A36 0%, #DC2626 50%, #7A1A36 100%)" }}
          aria-hidden="true"
        />
        <p className="text-[11px] uppercase tracking-[0.28em] mb-3"
           style={{ color: "#FCA5A5" }}>
          {t.module_label}
        </p>
        <h2 className="font-serif text-3xl sm:text-4xl mb-3 leading-tight">
          {t.timeup_title}
        </h2>
        <p className="text-base opacity-90 mb-1">{t.timeup_subtitle}</p>
        <p className="text-sm opacity-70">
          {isLast ? t.timeup_last : t.timeup_next}
        </p>
      </div>
    </motion.div>
  );
}

// ===== Runner: in-progress exam (per-module pacing) =====
//
// Phase 5 redesign: the exam is now run module-by-module. The student is
// locked to `attempt.current_slot_index`; previous modules are read-only
// (locked) and future modules aren't visible. Each module has its own
// 60-min countdown (`attempt.current_slot_expires_at`). When the
// countdown hits 0 (or the user clicks "Conferma modulo"), we POST
// `/advance-slot` — the backend locks the answers and bumps to the next
// module. On the LAST module, advance-slot finalises the attempt and we
// transition to the summary view via `onAttemptUpdate`.
function RunnerView({ t, locale, attempt, onAttemptUpdate, onSubmit, submitting }) {
  const [questionIdx, setQuestionIdx] = useState(0);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  const [savingUid, setSavingUid] = useState(null);
  const [warningShown, setWarningShown] = useState(false);
  const [warningDismissed, setWarningDismissed] = useState(false);
  const [advancing, setAdvancing] = useState(false);
  const advancingRef = useRef(false);
  // ─── Phase 8 overlays ───
  const [showTimeUp, setShowTimeUp] = useState(false);
  const [showTransition, setShowTransition] = useState(false);
  // "La commissione sta aspettando" — appears after 90s of no interaction
  // on the current question. Reset on answer / nav.
  const [showWaitingHint, setShowWaitingHint] = useState(false);
  const lastInteractionRef = useRef(Date.now());
  const prevSlotIdxRef = useRef(null);

  // ─── Exam incident banners ("el profesor pasa", "el compañero copia"...) ──
  // Schedules a random incident every 4-6 minutes of active exam time,
  // creating immersive pressure that mirrors the real ministerial exam.
  // Static content (no LLM runtime cost). Each banner key is recorded so
  // we don't repeat ones already seen in this attempt.
  const [activeIncident, setActiveIncident] = useState(null);
  const seenIncidentsRef = useRef(new Set());
  const nextIncidentAtRef = useRef(null);

  const slots = attempt.slots || [];
  const slotIdx = Math.max(0, Math.min(slots.length - 1,
    Number(attempt.current_slot_index ?? 0)));
  const currentSlot = slots[slotIdx] || { questions: [] };
  const currentQuestion = currentSlot.questions[questionIdx];
  const isLastSlot = slotIdx === slots.length - 1;

  // Reset question pointer + warning state when the active module changes.
  useEffect(() => {
    setQuestionIdx(0);
    setWarningShown(false);
    setWarningDismissed(false);
    setActiveIncident(null);
    // Schedule the FIRST incident for this module 90–180s after entering.
    nextIncidentAtRef.current = Date.now() + (90 + Math.random() * 90) * 1000;
  }, [slotIdx, attempt.attempt_id]);

  // ─── Anti-copy / anti-screenshot defences (Phase 6.2 security) ──
  // Free users have unlimited attempts to game the system — this layer
  // makes casual scraping painful. It does NOT prevent a determined
  // attacker (they could read the DOM via DevTools), but combined with
  // the server-side strike system on the videos and the "no descargas"
  // policy it's enough deterrent for an MVP. Hooks attach on mount.
  useEffect(() => {
    const blockContext = (e) => { e.preventDefault(); };
    const blockSelect = (e) => { e.preventDefault(); };
    const blockCopy = (e) => {
      e.preventDefault();
      toast.warning(locale === "es"
        ? "🚫 Copiar las preguntas está bloqueado durante el examen."
        : "🚫 Copiare le domande è bloccato durante l'esame.");
    };
    const blockKey = (e) => {
      // Ctrl/Cmd+C, +X, +S, +P, +A, +U, F12 — the usual scraping shortcuts.
      const k = (e.key || "").toLowerCase();
      const mod = e.ctrlKey || e.metaKey;
      if (mod && ["c", "x", "s", "p", "a", "u"].includes(k)) {
        e.preventDefault();
        toast.warning(locale === "es"
          ? "🚫 Esta acción está bloqueada durante el examen."
          : "🚫 Questa azione è bloccata durante l'esame.");
      }
      if (e.key === "F12" || (mod && e.shiftKey && k === "i")) {
        e.preventDefault();
      }
    };
    document.addEventListener("contextmenu", blockContext);
    document.addEventListener("selectstart", blockSelect);
    document.addEventListener("copy", blockCopy);
    document.addEventListener("keydown", blockKey);
    return () => {
      document.removeEventListener("contextmenu", blockContext);
      document.removeEventListener("selectstart", blockSelect);
      document.removeEventListener("copy", blockCopy);
      document.removeEventListener("keydown", blockKey);
    };
  }, [locale]);

  // Per-module countdown. Prefer the server-computed
  // `current_slot_expires_at`; fall back to the legacy `expires_at` if
  // the field is missing (e.g., an attempt created before this release).
  const expiryIso = attempt.current_slot_expires_at || attempt.expires_at;
  useEffect(() => {
    const update = () => {
      if (!expiryIso) { setSecondsLeft(0); return; }
      const exp = new Date(expiryIso).getTime();
      const now = Date.now();
      setSecondsLeft(Math.max(0, Math.round((exp - now) / 1000)));
    };
    update();
    const id = setInterval(update, 1000);
    return () => clearInterval(id);
  }, [expiryIso]);

  // 10-minute warning banner: fires ONCE when the countdown crosses the
  // threshold from above. The user can dismiss it, and dismissed state
  // is reset only when the module changes.
  useEffect(() => {
    if (secondsLeft > 0 && secondsLeft <= 600 && !warningShown) {
      setWarningShown(true);
    }
  }, [secondsLeft, warningShown]);

  // Schedule next incident banner with ESCALATING frequency, like an
  // airline pilot simulator: the further into the exam, the more often
  // distractions hit. First banner 90-180s in; subsequent banners use
  // max(60, 360 - seenCount * 45) seconds gap. Crucially the banner is
  // sticky (no auto-dismiss): the user MUST click to remove it, just
  // like a real proctor.
  useEffect(() => {
    if (!secondsLeft) return;
    if (activeIncident) return;  // one at a time
    if (secondsLeft <= 600) return;  // let the "fogli" warning own the UI
    const now = Date.now();
    if (!nextIncidentAtRef.current || now < nextIncidentAtRef.current) return;
    const incident = pickRandomIncident(seenIncidentsRef.current);
    if (incident) {
      seenIncidentsRef.current.add(incident.key);
      setActiveIncident(incident);
    }
    // Next gap shrinks as the user sees more banners (max 6 min, min 1 min).
    const seen = seenIncidentsRef.current.size;
    const gapSec = Math.max(60, 360 - seen * 45) + Math.random() * 30;
    nextIncidentAtRef.current = now + gapSec * 1000;
  }, [secondsLeft, activeIncident]);

  const handleIncidentDismiss = () => {
    setActiveIncident(null);
    // Re-arm the next-incident timer NOW that the user dismissed. Using
    // the same escalating gap formula above.
    const seen = seenIncidentsRef.current.size;
    const gapSec = Math.max(60, 360 - seen * 45) + Math.random() * 30;
    nextIncidentAtRef.current = Date.now() + gapSec * 1000;
  };

  const doAdvance = async () => {
    if (advancingRef.current) return;
    advancingRef.current = true;
    setAdvancing(true);
    try {
      const res = await apiPost(`/vip/exam/${attempt.attempt_id}/advance-slot`);
      if (res?.attempt) onAttemptUpdate(res.attempt);
    } finally {
      setAdvancing(false);
      advancingRef.current = false;
    }
  };

  // Auto-advance when the per-module timer hits 0. The backend ALSO
  // catches this on read (via has_in_progress), but advancing locally
  // gives the student instant feedback. We surface a brief "Tempo
  // scaduto" overlay first so it feels intentional and dignified rather
  // than an abrupt swap.
  useEffect(() => {
    if (secondsLeft === 0 && expiryIso && !advancingRef.current && !showTimeUp) {
      const exp = new Date(expiryIso).getTime();
      if (Date.now() >= exp) {
        setShowTimeUp(true);
        const tid = setTimeout(() => {
          setShowTimeUp(false);
          doAdvance();
        }, 1600);
        return () => clearTimeout(tid);
      }
    }
  }, [secondsLeft, expiryIso, showTimeUp]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalQuestions = useMemo(
    () => slots.reduce((s, sl) => s + sl.questions.length, 0), [slots],
  );
  const answeredCount = useMemo(
    () => Object.keys(attempt.answers || {}).length, [attempt.answers],
  );
  const answeredInCurrentSlot = useMemo(
    () => currentSlot.questions.filter(
      (q) => (attempt.answers || {})[q.uid] !== undefined,
    ).length,
    [currentSlot, attempt.answers],
  );

  const goPrev = () => { markInteraction(); if (questionIdx > 0) setQuestionIdx(questionIdx - 1); };
  const goNext = () => {
    markInteraction();
    if (questionIdx < currentSlot.questions.length - 1) {
      setQuestionIdx(questionIdx + 1);
    }
  };
  const isLastQuestionInSlot = questionIdx === currentSlot.questions.length - 1;

  const selectAnswer = async (uid, optIdx) => {
    if (savingUid) return;
    markInteraction();
    setSavingUid(uid);
    // Optimistic update.
    const newAnswers = { ...(attempt.answers || {}), [uid]: optIdx };
    onAttemptUpdate({ ...attempt, answers: newAnswers });
    try {
      await apiPost(`/vip/exam/${attempt.attempt_id}/answer`,
                    { uid, selected_index: optIdx });
    } finally {
      setSavingUid(null);
    }
  };

  const unansweredCount = currentSlot.questions.length - answeredInCurrentSlot;
  const tension = useMemo(() => tensionLevel(secondsLeft), [secondsLeft]);
  const palette = TENSION_PALETTE[tension.tone];
  const isCritical = tension.tone === "critical" || tension.tone === "imminent";

  // ─── Module transition overlay ───
  // Fires when current_slot_index changes (after advance-slot succeeds).
  // First mount: just record the slot, no overlay (the student is just
  // arriving at the first module).
  useEffect(() => {
    if (prevSlotIdxRef.current === null) {
      prevSlotIdxRef.current = slotIdx;
      return;
    }
    if (prevSlotIdxRef.current !== slotIdx && !isLastSlot) {
      // We moved FROM a previous slot to a new one. Show transition.
      setShowTransition(true);
      const tid = setTimeout(() => setShowTransition(false), 2400);
      return () => clearTimeout(tid);
    }
    prevSlotIdxRef.current = slotIdx;
  }, [slotIdx, isLastSlot]);

  // ─── "La commissione sta aspettando" idle hint ───
  // Resets on every answer / nav click. Shows subtle text under the timer
  // after 90s of inactivity. NO animation, NO bouncing — just appears.
  useEffect(() => {
    setShowWaitingHint(false);
    lastInteractionRef.current = Date.now();
    const id = setInterval(() => {
      if (Date.now() - lastInteractionRef.current >= 90_000) {
        setShowWaitingHint(true);
      }
    }, 5_000);
    return () => clearInterval(id);
  }, [questionIdx, slotIdx, attempt.attempt_id]);

  const markInteraction = () => {
    lastInteractionRef.current = Date.now();
    setShowWaitingHint(false);
  };

  return (
    <motion.section data-testid="vip-exam-runner"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-4xl mx-auto">
      {/* Module transition + time-up overlays */}
      {showTransition && (
        <ModuleTransitionOverlay t={t} nextSlotIndex={slotIdx}
                                 totalSlots={slots.length}
                                 slotLabel={slotLabel(currentSlot, locale)} />
      )}
      {showTimeUp && <TimeUpOverlay t={t} isLast={isLastSlot} />}

      {/* Sticky header with timer + progress — glassmorphism for premium feel */}
      <div className="sticky top-0 z-20 py-3 mb-6 -mx-3 sm:-mx-6 px-3 sm:px-6"
           style={{
             background: palette.headerBg,
             backdropFilter: "blur(18px) saturate(140%)",
             WebkitBackdropFilter: "blur(18px) saturate(140%)",
             borderBottom: `1px solid ${palette.headerRim}`,
             boxShadow: "0 1px 0 rgba(255,255,255,0.4) inset",
           }}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-medium ring-1"
                  style={{ background: "rgba(237, 231, 255, 0.8)",
                           color: "#5A3FC0",
                           ['--tw-ring-color']: "rgba(90,63,192,0.18)" }}
                  data-testid="vip-exam-progress">
              <ListChecks className="h-3.5 w-3.5" /> {t.progress_label}: {answeredCount}/{totalQuestions}
            </span>
            <span className="text-xs text-brand-secondary uppercase tracking-wide">
              {t.module_label} {slotIdx + 1}/{slots.length} · {t.question_label} {questionIdx + 1}/{currentSlot.questions.length}
            </span>
          </div>
          <span className={[
                  "inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-semibold tabular-nums ring-1",
                  tension.pulse ? "animate-pulse" : "",
                ].join(" ")}
                style={{
                  background: palette.chipBg,
                  color: palette.chipFg,
                  ['--tw-ring-color']: palette.chipRing,
                }}
                data-testid="vip-exam-timer"
                data-tension={tension.tone}>
            <Clock className="h-4 w-4" /> {t.module_timer_label}: {formatHMS(secondsLeft)}
          </span>
        </div>
        {/* Thin progress bar at the bottom of the glass header */}
        <div className="mt-2 h-1 rounded-full overflow-hidden"
             style={{ background: "rgba(229, 220, 239, 0.5)" }}
             aria-hidden="true">
          <div className="h-full transition-[width] duration-300 rounded-full"
               style={{
                 width: `${Math.round((answeredCount / Math.max(totalQuestions, 1)) * 100)}%`,
                 background: palette.barFill,
               }} />
        </div>
        {/* Subtle "la commissione sta aspettando" line — only in calm/watch
            tones (don't add cognitive load when the candidate is already
            sweating in the critical/imminent zone). */}
        {showWaitingHint && !isCritical && (
          <p className="mt-2 text-[11px] text-brand-secondary italic text-center tracking-wide"
             data-testid="vip-exam-commission-waiting">
            {t.commission_waiting}
          </p>
        )}
      </div>

      {/* Random immersion incident — escalating frequency, NO auto-
          dismiss. The user MUST click ✕ or "Ho capito" to remove it.
          Pulses subtly after the 3rd banner to ratchet up the pressure. */}
      <ExamIncidentBanner incident={activeIncident}
                          language={locale}
                          pulse={seenIncidentsRef.current.size >= 3}
                          onDismiss={handleIncidentDismiss} />

      {/* 10-min warning banner — appears once when ≤10 min remain. */}
      {warningShown && !warningDismissed && secondsLeft > 0 && (
        <div data-testid="vip-exam-time-warning"
             className="max-w-4xl mx-auto mb-4 rounded-2xl border px-4 py-3 sm:px-5 sm:py-4 flex items-start gap-3"
             style={{
               background: "linear-gradient(135deg, #FCE9EE 0%, #FFF1F4 100%)",
               borderColor: "rgba(158, 42, 72, 0.35)",
               color: "#7A1A36",
             }}>
          <AlertCircle className="h-5 w-5 mt-0.5 shrink-0" style={{ color: "#9E2A48" }} />
          <div className="flex-1">
            <p className="font-semibold mb-0.5">{t.module_warning_title}</p>
            <p className="text-sm leading-relaxed">{t.module_warning_body}</p>
          </div>
          <button onClick={() => setWarningDismissed(true)}
                  data-testid="vip-exam-time-warning-ack"
                  className="text-xs font-semibold underline whitespace-nowrap"
                  style={{ color: "#9E2A48" }}>
            {t.module_warning_ack}
          </button>
        </div>
      )}

      {/* Read-only module strip: shows which modules are locked / current /
          not-started. NOT clickable — pacing is enforced server-side. */}
      <div className="flex flex-wrap gap-2 mb-4" data-testid="vip-exam-modules-nav">
        {slots.map((s, i) => {
          const isActive = i === slotIdx;
          const isLocked = i < slotIdx;
          const isFuture = i > slotIdx;
          const answeredHere = s.questions.filter(
            (q) => (attempt.answers || {})[q.uid] !== undefined,
          ).length;
          return (
            <span key={s.slot_id}
                  data-testid={`vip-exam-slot-tab-${s.slot_id}`}
                  className={[
                    "px-3 py-1.5 rounded-full text-xs font-medium border inline-flex items-center gap-1.5",
                    isActive
                      ? "bg-brand-primary text-white border-brand-primary"
                      : isLocked
                        ? "bg-gray-100 text-gray-500 border-gray-200"
                        : "bg-white text-gray-400 border-gray-200",
                  ].join(" ")}>
              {isLocked ? <Lock className="h-3 w-3" /> : null}
              {slotLabel(s, locale)}
              {isLocked ? <span className="ml-1 opacity-70">· {answeredHere}/{s.questions.length}</span> : null}
              {isFuture ? <span className="ml-1 opacity-70">· —</span> : null}
              {isActive ? <span className="ml-1 opacity-90">· {answeredHere}/{s.questions.length}</span> : null}
            </span>
          );
        })}
      </div>

      {/* Question card */}
      <div className="rounded-2xl border border-brand-border bg-white shadow-premium p-6 sm:p-8 mb-6"
           data-testid="vip-exam-question-card">
        <p className="text-xs uppercase tracking-[0.18em] text-brand-secondary mb-2">
          {slotLabel(currentSlot, locale)}
        </p>
        <h2 className="font-serif text-xl sm:text-2xl text-brand-primary leading-snug mb-5"
            data-testid="vip-exam-question-stem">
          {currentQuestion?.stem}
        </h2>
        <div className="space-y-2">
          {(currentQuestion?.options || []).map((opt, i) => {
            const selected = (attempt.answers || {})[currentQuestion.uid] === i;
            return (
              <button key={i}
                      onClick={() => selectAnswer(currentQuestion.uid, i)}
                      data-testid={`vip-exam-option-${i}`}
                      className={[
                        "block w-full text-left px-4 py-3 rounded-xl border transition-colors",
                        selected
                          ? "bg-brand-primary text-white border-brand-primary"
                          : "bg-white text-brand-primary border-brand-border hover:border-brand-primary",
                      ].join(" ")}>
                <span className="font-medium mr-2">{String.fromCharCode(65 + i)}.</span>
                <span>{opt}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Navigation — within the current module only */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <button onClick={goPrev} disabled={questionIdx === 0}
                data-testid="vip-exam-nav-prev"
                className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl border border-brand-border bg-white text-brand-primary disabled:opacity-40 hover:border-brand-primary">
          <ChevronLeft className="h-4 w-4" /> {t.nav_prev}
        </button>

        {isLastQuestionInSlot ? (
          <button onClick={() => setConfirmOpen(true)} disabled={advancing || submitting}
                  data-testid="vip-exam-advance-btn"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-brand-green text-white font-semibold hover:opacity-90 disabled:opacity-50">
            {advancing
              ? <Loader2 className="h-4 w-4 animate-spin" />
              : <Send className="h-4 w-4" />}
            {isLastSlot ? t.module_advance_last_cta : t.module_advance_cta}
          </button>
        ) : (
          <button onClick={goNext}
                  data-testid="vip-exam-nav-next"
                  className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-green transition-colors">
            {t.nav_next} <ChevronRight className="h-4 w-4" />
          </button>
        )}
      </div>

      {/* "Confirm module" CTA always available from desktop sidebar */}
      {!isLastQuestionInSlot && answeredInCurrentSlot > 0 && (
        <div className="mt-4 text-right">
          <button onClick={() => setConfirmOpen(true)} disabled={advancing || submitting}
                  data-testid="vip-exam-advance-link"
                  className="text-sm text-brand-secondary underline hover:text-brand-primary">
            {isLastSlot ? t.module_advance_last_cta : t.module_advance_cta}
          </button>
        </div>
      )}

      {/* Confirm advance modal — non-final modules only */}
      {confirmOpen && !isLastSlot && (
        <div data-testid="vip-exam-confirm-modal"
             className="fixed inset-0 z-50 grid place-items-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-premium-lg max-w-md w-full p-6">
            <h3 className="font-serif text-2xl text-brand-primary mb-2">
              {t.module_advance_confirm_title}
            </h3>
            <p className="text-brand-secondary mb-2">{t.module_advance_confirm_desc}</p>
            {unansweredCount > 0 && (
              <p className="text-sm mb-2 inline-flex items-center gap-1.5"
                 style={{ color: "#9E2A48" }}>
                <AlertCircle className="h-4 w-4" />
                {t.confirm_finish_unanswered.replace("{n}", unansweredCount)}
              </p>
            )}
            <div className="flex justify-end gap-2 mt-5">
              <button onClick={() => setConfirmOpen(false)}
                      data-testid="vip-exam-confirm-cancel"
                      className="px-4 py-2 rounded-xl border border-brand-border text-brand-primary hover:bg-brand-bg">
                {t.module_advance_confirm_no}
              </button>
              <button onClick={() => { setConfirmOpen(false); doAdvance(); }}
                      disabled={advancing || submitting}
                      data-testid="vip-exam-confirm-advance"
                      className="px-4 py-2 rounded-xl bg-brand-green text-white font-semibold hover:opacity-90 disabled:opacity-50 inline-flex items-center gap-2">
                {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                {t.module_advance_confirm_yes}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Last-module CELEBRATION modal — mixes congrats + provisional
          score + upsell (more attempts / Amazon book / AIMS pack). */}
      {confirmOpen && isLastSlot && (
        <FinalModuleModal t={t}
                          totalAnswered={answeredCount}
                          totalQuestions={totalQuestions}
                          packs={attempt._packs}
                          advancing={advancing}
                          submitting={submitting}
                          onCancel={() => setConfirmOpen(false)}
                          onConfirm={() => { setConfirmOpen(false); doAdvance(); }} />
      )}
    </motion.section>
  );
}

// ============================================================================
// FinalModuleModal — shown when the student confirms the LAST module.
// Mirrors the airline-pilot-simulator metaphor: at the end of a stressful
// run, you get a debrief. Provisional score is computed client-side from
// answered/total ratio so we can pivot the message without waiting for
// the backend submit. Upsell stack:
//   1) Buy more attempts (Stripe Payment Link from `packs.exam_pack_20`).
//   2) Amazon book by prof. Terzano (hard-coded link).
//   3) AIMS €9.99 pack — Stripe link is OPTIONAL (env var
//      STRIPE_PAYMENT_LINK_AIMS_PACK), shows "Disponibile a breve" when
//      missing so we can ship this UI immediately without the checkout.
// ============================================================================
function FinalModuleModal({ t, totalAnswered, totalQuestions, packs,
                            advancing, submitting, onCancel, onConfirm }) {
  const [aimsUrl, setAimsUrl] = useState(null);
  useEffect(() => {
    // Read the optional AIMS Stripe URL from the credits endpoint.
    apiGet("/vip/exam/aims-pack-url").then((r) => setAimsUrl(r?.url || null))
                                     .catch(() => setAimsUrl(null));
  }, []);
  const pct = totalQuestions
    ? Math.round((totalAnswered / totalQuestions) * 100) : 0;
  const tier = pct >= 80 ? "great" : pct >= 55 ? "ok" : "low";
  const tierColor = { great: "#107F52", ok: "#B45309", low: "#9E2A48" }[tier];
  const scoreMsg = {
    great: t.final_modal_score_great,
    ok: t.final_modal_score_ok,
    low: t.final_modal_score_low,
  }[tier];
  const explainMsg = tier === "low" ? t.final_modal_explain_low : t.final_modal_explain_great;
  const buyAttemptsUrl = packs?.exam_pack_20?.url || packs?.exam_pack_10?.url || null;
  const amazonUrl = "https://www.amazon.it/TEORICO-PRATICA-SUPERARE-LESAME-RICONOSCIMENTO-MEDICINA-ebook/dp/B0DM2QSN1Z";

  return (
    <div data-testid="vip-exam-final-modal"
         className="fixed inset-0 z-50 grid place-items-center bg-black/60 backdrop-blur-sm p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl shadow-premium-lg max-w-lg w-full my-6">
        {/* Italian flag accent bar */}
        <div className="h-1 w-full flex rounded-t-2xl overflow-hidden" aria-hidden="true">
          <div className="flex-1" style={{ background: "#008C45" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#CD212A" }} />
        </div>
        <div className="p-6 space-y-4">
          <div>
            <h3 className="font-serif text-2xl text-brand-primary mb-1">
              {t.final_modal_title}
            </h3>
            <p className="text-brand-secondary text-sm">{t.final_modal_subtitle}</p>
          </div>

          {/* Provisional score chip */}
          <div className="rounded-xl border p-3 flex items-center justify-between"
               style={{ borderColor: `${tierColor}33`, background: `${tierColor}0d` }}>
            <div>
              <p className="text-[11px] uppercase tracking-wide opacity-70"
                 style={{ color: tierColor }}>
                {t.final_modal_provisional}
              </p>
              <p className="text-xl font-bold tabular-nums" style={{ color: tierColor }}>
                {totalAnswered}/{totalQuestions} · {pct}%
              </p>
            </div>
            <span className="font-semibold text-sm" style={{ color: tierColor }}>
              {scoreMsg}
            </span>
          </div>
          <p className="text-sm text-brand-primary leading-relaxed">{explainMsg}</p>

          {/* Upsell #1: more attempts (only when score is low) */}
          {tier !== "great" && buyAttemptsUrl && (
            <a href={buyAttemptsUrl} target="_blank" rel="noopener noreferrer"
               data-testid="vip-final-buy-attempts"
               className="block rounded-xl p-3 border hover:scale-[1.01] transition-transform"
               style={{ background: "linear-gradient(135deg, #FCE9EE 0%, #FFF1F4 100%)",
                        borderColor: "rgba(158,42,72,0.35)" }}>
              <span className="font-semibold text-sm" style={{ color: "#9E2A48" }}>
                🎯 {t.final_modal_buy_attempts_cta}
              </span>
            </a>
          )}

          {/* Amazon book */}
          <a href={amazonUrl} target="_blank" rel="noopener noreferrer"
             data-testid="vip-final-amazon-cta"
             className="block rounded-xl p-3 border hover:scale-[1.01] transition-transform"
             style={{ background: "linear-gradient(135deg,#FFF7E6 0%,#FFEED1 100%)",
                      borderColor: "rgba(180,83,9,0.35)" }}>
            <p className="font-semibold text-sm mb-0.5" style={{ color: "#7A4F00" }}>
              {t.final_modal_amazon_title}
            </p>
            <p className="text-xs leading-relaxed mb-1.5" style={{ color: "#5A3A00" }}>
              {t.final_modal_amazon_desc}
            </p>
            <span className="text-xs font-semibold inline-flex items-center gap-1"
                  style={{ color: "#7A4F00", textDecoration: "underline" }}>
              {t.final_modal_amazon_cta} →
            </span>
          </a>

          {/* AIMS €9,99 — graceful when STRIPE URL is not yet configured */}
          <div className="rounded-xl p-3 border"
               data-testid="vip-final-aims-card"
               style={{ background: "linear-gradient(135deg,#F0EAFF 0%,#E0D4FA 100%)",
                        borderColor: "rgba(90,63,192,0.4)" }}>
            <p className="font-semibold text-sm mb-0.5" style={{ color: "#3A2A8A" }}>
              {t.final_modal_aims_title}
            </p>
            <p className="text-xs leading-relaxed mb-2" style={{ color: "#2A1F4A" }}>
              {t.final_modal_aims_desc}
            </p>
            {aimsUrl ? (
              <a href={aimsUrl} target="_blank" rel="noopener noreferrer"
                 data-testid="vip-final-aims-cta"
                 className="inline-block px-3 py-1.5 rounded-lg text-white text-xs font-semibold"
                 style={{ background: "#5A3FC0" }}>
                {t.final_modal_aims_cta} →
              </a>
            ) : (
              <span className="inline-block px-3 py-1.5 rounded-lg text-xs italic"
                    style={{ background: "rgba(90,63,192,0.15)", color: "#3A2A8A" }}>
                {t.final_modal_aims_unavailable}
              </span>
            )}
          </div>

          <div className="flex justify-end gap-2 pt-1">
            <button onClick={onCancel}
                    data-testid="vip-final-cancel"
                    className="px-4 py-2 rounded-xl border border-brand-border text-brand-primary hover:bg-brand-bg text-sm">
              {t.final_modal_cancel}
            </button>
            <button onClick={onConfirm}
                    disabled={advancing || submitting}
                    data-testid="vip-final-confirm"
                    className="px-5 py-2 rounded-xl text-white font-semibold disabled:opacity-50 inline-flex items-center gap-2 text-sm"
                    style={{ background: "linear-gradient(135deg,#00A052,#008C45)" }}>
              {advancing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
              {t.final_modal_confirm_cta}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ===== Certificate card (shown when attempt.passed) =====
function CertificateCard({ t, attempt }) {
  const [busy, setBusy] = React.useState(false);
  const certId = attempt?.certificate_id;
  if (!certId) return null;
  const apiBase = process.env.REACT_APP_BACKEND_URL || "";
  const downloadUrl = `${apiBase}/api/vip/exam/certificates/${certId}/download`;

  const onDownload = async () => {
    setBusy(true);
    try {
      // Trigger a real download via fetch + blob so credentials are sent and
      // a "Save as" dialog opens with the proper filename.
      const r = await fetch(downloadUrl, { credentials: "include" });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const blob = await r.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `certificato-simulatore-${certId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      // Fallback: open in a new tab so the browser handles the PDF natively.
      window.open(downloadUrl, "_blank", "noopener");
    }
    setBusy(false);
  };

  return (
    <motion.div data-testid="vip-exam-certificate-card"
                initial="hidden" animate="show" variants={FADE_UP}
                className="rounded-2xl shadow-premium-lg p-6 sm:p-8 mb-6 text-white"
                style={{
                  background: "linear-gradient(135deg, #2C1862 0%, #5A3FC0 60%, #B45309 100%)",
                }}>
      <div className="flex items-start gap-4 flex-wrap">
        <div className="h-14 w-14 rounded-2xl grid place-items-center shrink-0"
             style={{ background: "rgba(255,255,255,0.18)", backdropFilter: "blur(8px)" }}>
          <Award className="h-7 w-7 text-white" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-[10px] uppercase tracking-[0.22em] opacity-90 mb-1 inline-flex items-center gap-2">
            <ShieldCheck className="h-3 w-3" /> {t.cert_eyebrow}
          </p>
          <h3 className="font-serif text-2xl sm:text-3xl leading-tight">
            {t.cert_title_card}
          </h3>
          <p className="text-sm sm:text-base opacity-90 leading-relaxed mt-2">
            {t.cert_desc}
          </p>
          <p className="text-[11px] opacity-75 mt-2 font-mono">
            {certId}
          </p>
        </div>
        <button onClick={onDownload} disabled={busy}
                data-testid="vip-exam-certificate-download"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-white text-brand-primary font-semibold whitespace-nowrap hover:opacity-90 disabled:opacity-60 disabled:cursor-not-allowed">
          {busy
            ? <><Loader2 className="h-4 w-4 animate-spin" /> {t.cert_downloading}</>
            : <><Download className="h-4 w-4" /> {t.cert_download}</>}
        </button>
      </div>
    </motion.div>
  );
}

// ===== Achievement badges =====
// Computed client-side from the user's history list + the current attempt.
// Cheap to recompute on every render — at most ~50 attempts in the list.
// Phase 4 polish: gives passing attempts a celebratory, premium-feeling
// secondary surface besides the certificate card.
function computeAchievements(attempt, history) {
  if (!attempt?.passed) return [];
  const aid = attempt.attempt_id;
  const sortedPassed = (history || [])
    .filter((a) => a.status === "submitted" && a.passed)
    .sort((a, b) => (a.submitted_at || a.started_at)
      .localeCompare(b.submitted_at || b.started_at));
  const passedIndex = sortedPassed.findIndex((a) => a.attempt_id === aid);
  const result = attempt.result || {};

  const out = [];
  // First-pass: this attempt is the user's first ever passing submit.
  if (passedIndex === 0
      || (passedIndex === -1 && sortedPassed.length === 0)) {
    out.push({
      id: "first_pass",
      icon: Star,
      tone: "violet",
    });
  }
  // Sin errores: perfect score.
  if (result.total_correct
      && result.total_questions
      && result.total_correct === result.total_questions) {
    out.push({ id: "perfect", icon: Target, tone: "gold" });
  }
  // Triplete: this is the 3rd (or later) consecutive passing attempt with no
  // failing attempt in between. Tightened: look at the last 3 SUBMITTED
  // attempts including this one. If all 3 are passing → unlock.
  const sortedSubmitted = (history || [])
    .filter((a) => a.status === "submitted")
    .sort((a, b) => (b.submitted_at || b.started_at)
      .localeCompare(a.submitted_at || a.started_at)); // desc
  const here = sortedSubmitted.findIndex((a) => a.attempt_id === aid);
  if (here !== -1) {
    const lastThree = sortedSubmitted.slice(here, here + 3);
    if (lastThree.length === 3 && lastThree.every((a) => a.passed)) {
      out.push({ id: "triplete", icon: Flame, tone: "rose" });
    }
  }
  return out;
}

const BADGE_TONES = {
  violet: { bg: "linear-gradient(135deg, #4B2EA4 0%, #7E5BD9 100%)", glow: "rgba(126,91,217,0.45)" },
  gold:   { bg: "linear-gradient(135deg, #B45309 0%, #F5C76A 100%)", glow: "rgba(245,199,106,0.5)"  },
  rose:   { bg: "linear-gradient(135deg, #9E2A48 0%, #E25C7E 100%)", glow: "rgba(226,92,126,0.45)" },
};

function AchievementBadges({ t, achievements }) {
  if (!achievements?.length) return null;
  return (
    <div data-testid="vip-exam-achievements"
         className="rounded-2xl p-5 sm:p-6 mb-6 border"
         style={{ background: "white", borderColor: "#E5DCEF" }}>
      <h3 className="font-serif text-lg sm:text-xl text-brand-primary mb-4 inline-flex items-center gap-2">
        <Sparkles className="h-4 w-4" style={{ color: "#C8A04A" }} />
        {t.badge_section}
      </h3>
      <div className="grid sm:grid-cols-3 gap-3">
        {achievements.map((a, i) => {
          const Icon = a.icon;
          const tone = BADGE_TONES[a.tone] || BADGE_TONES.violet;
          const title = t[`badge_${a.id}_title`] || a.id;
          const desc = t[`badge_${a.id}_desc`] || "";
          return (
            <motion.div
              key={a.id}
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: 0.12 * i, type: "spring", stiffness: 180 }}
              data-testid={`vip-exam-achievement-${a.id}`}
              className="rounded-xl p-4 text-white relative overflow-hidden"
              style={{
                background: tone.bg,
                boxShadow: `0 8px 24px ${tone.glow}, inset 0 1px 0 rgba(255,255,255,0.16)`,
              }}>
              {/* subtle gleam */}
              <div className="absolute -top-10 -right-10 h-24 w-24 rounded-full opacity-25 blur-2xl"
                   style={{ background: "white" }} aria-hidden="true" />
              <div className="flex items-start gap-3 relative">
                <div className="h-11 w-11 rounded-xl grid place-items-center shrink-0"
                     style={{ background: "rgba(255,255,255,0.18)",
                              backdropFilter: "blur(6px)" }}>
                  <Icon className="h-5 w-5" />
                </div>
                <div className="min-w-0">
                  <p className="font-serif text-lg leading-tight">{title}</p>
                  <p className="text-xs opacity-90 mt-1 leading-snug">{desc}</p>
                </div>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}

// ===== Phase 7 — Intro completed → motivational + CTA to real preview =====
function IntroDoneView({ t, locale, attempt, onStartPreview, starting, onBack }) {
  const result = attempt.result || {};
  const correct = result.total_correct ?? 0;
  const total = result.total_questions ?? attempt.questions_per_slot ?? 10;
  return (
    <div className="max-w-3xl mx-auto py-10" data-testid="vip-exam-intro-done">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55 }}
                  className="rounded-2xl overflow-hidden border shadow-xl"
                  style={{ borderColor: "rgba(184,134,47,0.4)" }}>
        {/* Italian tricolor accent bar */}
        <div className="flex h-2">
          <div className="flex-1" style={{ background: "#1A8A53" }} />
          <div className="flex-1" style={{ background: "#FFFFFF" }} />
          <div className="flex-1" style={{ background: "#C8102E" }} />
        </div>
        <div className="p-8" style={{ background: "linear-gradient(180deg,#FFFFFF 0%,#F8F5EE 100%)" }}>
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-2"
             style={{ color: "#9E1B32" }}>
            {t.intro_done_eyebrow}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl mb-2" style={{ color: "#1B1235" }}>
            {t.intro_done_title}
          </h1>
          <p className="font-serif text-xl italic mb-5" style={{ color: "#3A2C5A" }}>
            {t.intro_done_subtitle}
          </p>
          <div className="rounded-xl px-4 py-3 inline-flex items-center gap-2 text-sm mb-5"
               style={{ background: "rgba(184,134,47,0.12)", color: "#7a5b13" }}>
            <Trophy className="h-4 w-4" />
            {t.intro_done_score.replace("{n}", correct).replace("{total}", total)}
          </div>
          <p className="text-[15px] leading-relaxed mb-7" style={{ color: "#3A2C5A" }}>
            {t.intro_done_message}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            <button
              onClick={onStartPreview} disabled={starting}
              data-testid="vip-exam-intro-cta-real"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold text-white shadow-lg transition-transform hover:scale-[1.03] disabled:opacity-60"
              style={{
                background: "linear-gradient(135deg,#9E1B32 0%,#C8102E 45%,#B8862F 100%)",
                boxShadow: "0 12px 28px rgba(158,27,50,0.35)",
              }}>
              {starting ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" /> {t.intro_done_cta_loading}
                </>
              ) : (
                <>
                  <Flame className="h-5 w-5" /> {t.intro_done_cta_real}
                </>
              )}
            </button>
            <button onClick={onBack}
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-sm border"
                    style={{ borderColor: "#D4C8AC", color: "#5C4F36" }}>
              <ArrowLeft className="h-4 w-4" /> {t.intro_done_cta_back}
            </button>
          </div>
        </div>
      </motion.div>
    </div>
  );
}

// ===== Phase 7 — Preview locked (3 q done) → premium paywall =====
function PreviewLockedView({ t, packs }) {
  const features = t.preview_mode_locked_features || [];
  return (
    <div className="max-w-3xl mx-auto py-10" data-testid="vip-exam-preview-locked">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.55 }}
                  className="rounded-2xl overflow-hidden border-2 shadow-2xl"
                  style={{ borderColor: "#B8862F",
                           background: "linear-gradient(135deg,#1B1235 0%,#3A2C5A 100%)" }}>
        <div className="p-8 text-white">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-3"
             style={{ color: "#FFD27A" }}>
            <Lock className="inline h-3 w-3 mr-1" /> {t.preview_mode_locked_eyebrow}
          </p>
          <h1 className="font-serif text-3xl sm:text-4xl mb-3">
            {t.preview_mode_locked_title}
          </h1>
          <p className="text-base leading-relaxed mb-6 opacity-90">
            {t.preview_mode_locked_subtitle}
          </p>
          <ul className="grid sm:grid-cols-2 gap-2 mb-7">
            {features.map((f, i) => (
              <li key={i} className="flex items-start gap-2 text-sm"
                  data-testid={`vip-exam-preview-feature-${i}`}>
                <CheckCircle2 className="h-4 w-4 mt-0.5" style={{ color: "#FFD27A" }} />
                <span className="opacity-95">{f}</span>
              </li>
            ))}
          </ul>
          {packs ? (
            <div className="grid sm:grid-cols-2 gap-3">
              <PackCard pack={packs.exam_pack_10} t={t}
                        title={t.pack_10_title} price={t.pack_10_price}
                        cta={t.pack_10_cta} note={t.pack_10_note}
                        testId="vip-exam-preview-pack-10" />
              <PackCard pack={packs.exam_pack_20} t={t}
                        title={t.pack_20_title} price={t.pack_20_price}
                        cta={t.pack_20_cta} note={t.pack_20_note}
                        recommended testId="vip-exam-preview-pack-20" />
            </div>
          ) : null}
        </div>
      </motion.div>
    </div>
  );
}

// ===== Phase 7 — Device fingerprint blocked → paywall =====
function DeviceBlockedView({ t, packs }) {
  return (
    <div className="max-w-3xl mx-auto py-10" data-testid="vip-exam-device-blocked">
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.45 }}
                  className="rounded-2xl overflow-hidden border-2 shadow-xl"
                  style={{ borderColor: "rgba(184,134,47,0.6)",
                           background: "linear-gradient(180deg,#FFFFFF 0%,#FBF7EE 100%)" }}>
        <div className="p-8">
          <p className="text-[11px] uppercase tracking-[0.22em] font-semibold mb-2"
             style={{ color: "#9E1B32" }}>
            <ShieldCheck className="inline h-3 w-3 mr-1" /> {t.device_blocked_eyebrow}
          </p>
          <h1 className="font-serif text-3xl mb-2" style={{ color: "#1B1235" }}>
            {t.device_blocked_title}
          </h1>
          <p className="text-base leading-relaxed mb-6" style={{ color: "#3A2C5A" }}>
            {t.device_blocked_desc}
          </p>
          {packs ? (
            <div className="grid sm:grid-cols-2 gap-3 mb-5">
              <PackCard pack={packs.exam_pack_10} t={t}
                        title={t.pack_10_title} price={t.pack_10_price}
                        cta={t.pack_10_cta} note={t.pack_10_note}
                        testId="vip-exam-blocked-pack-10" />
              <PackCard pack={packs.exam_pack_20} t={t}
                        title={t.pack_20_title} price={t.pack_20_price}
                        cta={t.pack_20_cta} note={t.pack_20_note}
                        recommended testId="vip-exam-blocked-pack-20" />
            </div>
          ) : null}
          <p className="text-xs italic" style={{ color: "#7a6e57" }}>
            {t.device_blocked_help}
          </p>
        </div>
      </motion.div>
    </div>
  );
}

// ===== Summary: submitted exam =====
function SummaryView({ t, locale, attempt, history, onNewAttempt }) {
  const result = attempt.result || {};
  const passed = !!result.passed;
  return (
    <motion.section data-testid="vip-exam-summary"
                    initial="hidden" animate="show" variants={FADE_UP}
                    className="max-w-4xl mx-auto">
      <Link to="/vip/exam" className="inline-flex items-center gap-1.5 text-sm text-brand-secondary mb-4 hover:text-brand-primary"
            data-testid="vip-exam-back-link">
        <ArrowLeft className="h-4 w-4" /> {t.summary_back_home}
      </Link>

      {attempt?.passed ? <CertificateCard t={t} attempt={attempt} /> : null}
      {attempt?.passed ? (
        <AchievementBadges t={t}
                           achievements={computeAchievements(attempt, history)} />
      ) : null}

      <div className="rounded-2xl shadow-premium-lg p-6 sm:p-8 mb-6"
           style={{
             background: passed
               ? "linear-gradient(135deg, #E8F5EE 0%, #D7F0E2 100%)"
               : "linear-gradient(135deg, #FCE9EE 0%, #FDD8E0 100%)",
             border: `1px solid ${passed ? "#7DB89A" : "#E5A3B5"}`,
           }}>
        <div className="flex items-start gap-4 flex-wrap">
          <div className="h-14 w-14 rounded-2xl grid place-items-center"
               style={{ background: passed ? "#1A6B3A" : "#9E2A48" }}>
            {passed ? <Trophy className="h-7 w-7 text-white" />
                    : <XCircle className="h-7 w-7 text-white" />}
          </div>
          <div className="flex-1 min-w-0">
            <h1 className="font-serif text-3xl sm:text-4xl leading-tight"
                style={{ color: passed ? "#1A6B3A" : "#9E2A48" }}
                data-testid="vip-exam-summary-title">
              {passed ? t.summary_title_passed : t.summary_title_failed}
            </h1>
            <p className="text-brand-primary mt-2 leading-relaxed">
              {passed ? t.summary_desc_passed : t.summary_desc_failed}
            </p>
            <p className="font-serif text-2xl text-brand-primary mt-4"
               data-testid="vip-exam-summary-total">
              {t.summary_total}:{" "}
              <strong>{result.total_correct}/{result.total_questions}</strong>
            </p>
          </div>
        </div>
      </div>

      <h2 className="font-serif text-2xl text-brand-primary mb-3">
        {t.summary_per_module}
      </h2>
      <div className="grid sm:grid-cols-2 gap-3 mb-8" data-testid="vip-exam-per-module">
        {(result.per_slot || []).map((s) => {
          const sPassed = !!s.passed;
          const slotMeta = (attempt.slots || []).find((x) => x.slot_id === s.slot_id) || s;
          return (
            <div key={s.slot_id}
                 data-testid={`vip-exam-slot-result-${s.slot_id}`}
                 className="rounded-xl border bg-white p-4"
                 style={{ borderColor: sPassed ? "#7DB89A" : "#E5A3B5" }}>
              <div className="flex items-center justify-between gap-2">
                <span className="font-medium text-brand-primary">
                  {slotLabel(slotMeta, locale)}
                </span>
                <span className="text-xs px-2 py-0.5 rounded-full font-medium"
                      style={{
                        background: sPassed ? "#E8F5EE" : "#FCE9EE",
                        color: sPassed ? "#1A6B3A" : "#9E2A48",
                      }}>
                  {sPassed ? t.summary_passed_pill : t.summary_failed_pill}
                </span>
              </div>
              <p className="font-serif text-2xl text-brand-primary mt-2">
                {s.correct}/{s.total}
              </p>
              <p className="text-xs text-brand-secondary">
                {t.threshold_min}: {s.threshold || 18}/{s.total}
              </p>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-3 flex-wrap mb-10">
        <button onClick={onNewAttempt}
                data-testid="vip-exam-new-attempt"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-xl bg-brand-primary text-white font-semibold hover:bg-brand-green transition-colors">
          <Trophy className="h-4 w-4" /> {t.summary_new_attempt}
        </button>
        <Link to="/vip"
              data-testid="vip-exam-back-academy"
              className="inline-flex items-center gap-2 px-5 py-3 rounded-xl border border-brand-border bg-white text-brand-primary hover:border-brand-primary">
          <Crown className="h-4 w-4" /> {t.summary_back_home}
        </Link>
      </div>

      {/* Review */}
      <h2 className="font-serif text-2xl text-brand-primary mb-3">
        {t.summary_review_section}
      </h2>
      <div className="space-y-6" data-testid="vip-exam-review">
        {(attempt.slots || []).map((slot) => (
          <div key={slot.slot_id}>
            <h3 className="font-serif text-xl text-brand-primary mb-2">
              {slotLabel(slot, locale)}
            </h3>
            <div className="space-y-2">
              {slot.questions.map((q, qi) => {
                const userIdx = (attempt.answers || {})[q.uid];
                const correctIdx = q.correct_index;
                const isRight = userIdx === correctIdx;
                return (
                  <details key={q.uid}
                           data-testid={`vip-exam-review-${q.uid}`}
                           className="rounded-xl border border-brand-border bg-white p-4">
                    <summary className="cursor-pointer flex items-center gap-2">
                      {isRight
                        ? <CheckCircle2 className="h-4 w-4 text-brand-green shrink-0" />
                        : <XCircle className="h-4 w-4 shrink-0" style={{ color: "#9E2A48" }} />}
                      <span className="text-sm text-brand-primary">
                        <strong>Q{qi + 1}.</strong> {q.stem.slice(0, 120)}
                        {q.stem.length > 120 ? "…" : ""}
                      </span>
                    </summary>
                    <div className="mt-3 space-y-1">
                      {q.options.map((opt, oi) => {
                        const isCorrect = oi === correctIdx;
                        const isUserPick = oi === userIdx;
                        return (
                          <div key={oi}
                               className={[
                                 "px-3 py-2 rounded-lg text-sm border",
                                 isCorrect ? "border-brand-green bg-brand-green/10 text-brand-primary" :
                                 isUserPick ? "border-rose-300 bg-rose-50 text-brand-primary" :
                                 "border-brand-border bg-white text-brand-secondary",
                               ].join(" ")}>
                            <span className="font-medium mr-2">{String.fromCharCode(65 + oi)}.</span>
                            {opt}
                            {isCorrect && (
                              <span className="ml-2 text-xs font-medium" style={{ color: "#1A6B3A" }}>
                                · {t.summary_review_correct}
                              </span>
                            )}
                            {isUserPick && !isCorrect && (
                              <span className="ml-2 text-xs font-medium" style={{ color: "#9E2A48" }}>
                                · {t.summary_review_your}
                              </span>
                            )}
                          </div>
                        );
                      })}
                      {userIdx === undefined && (
                        <p className="text-xs italic text-brand-secondary mt-1">{t.answer_unanswered}</p>
                      )}
                      {q.explanation && (
                        <p className="text-sm text-brand-secondary mt-2 leading-relaxed">
                          {q.explanation}
                        </p>
                      )}
                    </div>
                  </details>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </motion.section>
  );
}

// ===== Hub page (list + start) =====
export default function VIPExam() {
  const navigate = useNavigate();
  // Phase 6.2: the written simulator ALWAYS runs in Italian — it
  // recreates the real ministerial exam, which is exclusively in
  // Italian. We do NOT honor user.preferred_language here on purpose.
  const language = "it";
  const [history, setHistory] = useState([]);
  const [active, setActive] = useState(null);
  const [credits, setCredits] = useState(null);
  const [packs, setPacks] = useState(null);
  const [loading, setLoading] = useState(true);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState(null);
  const [deviceBlocked, setDeviceBlocked] = useState(false);
  // Difficulty selection — free users are forced to "easy" server-side
  // (we mirror the gate here for UI clarity but the backend is the
  // source of truth).
  const [difficulty, setDifficulty] = useState("easy");

  const t = useMemo(() => UI[language] || UI.it, []);

  // Register the device fingerprint on first render. Result is cached
  // so subsequent calls are no-ops.
  useEffect(() => {
    registerDevice(apiPost).catch(() => {});
  }, []);

  // A user can pick medium/hard when they have ANY paid attempts OR
  // they're an admin / academy member. The credits payload tells us.
  const canPickDifficulty = !!(credits && credits.paid_left > 0);

  const refreshCredits = async () => {
    try {
      const c = await apiGet("/vip/exam/credits");
      setCredits(c?.credits || null);
      setPacks(c?.packs || null);
    } catch { /* leave as-is */ }
  };

  useEffect(() => {
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const [list, act, cr] = await Promise.all([
          apiGet("/vip/exam/attempts").catch(() => ({ attempts: [] })),
          apiGet("/vip/exam/active").catch(() => ({ attempt: null })),
          apiGet("/vip/exam/credits").catch(() => null),
        ]);
        if (!alive) return;
        setHistory(list?.attempts || []);
        setActive(act?.attempt || null);
        setCredits(cr?.credits || null);
        setPacks(cr?.packs || null);
        // Default difficulty: paid users get medium suggested, free stays easy.
        if ((cr?.credits?.paid_left || 0) > 0) setDifficulty("medium");
      } catch (e) {
        if (alive) setError(t.error_generic);
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleStart = async () => {
    setStarting(true);
    setError(null);
    try {
      // Include the device fingerprint so the backend can gate the free
      // intro attempt against multi-account abuse.
      const fingerprint = buildFingerprintComponents();
      const res = await apiPost("/vip/exam/start", { difficulty, fingerprint });
      const att = res?.attempt;
      if (res?.credits) setCredits(res.credits);
      if (att?.attempt_id) {
        navigate(`/vip/exam/${att.attempt_id}`);
      }
    } catch (e) {
      const status = e?.response?.status;
      const body = e?.response?.data || {};
      if (status === 402) {
        setCredits(body.credits || null);
        setPacks(body.packs || null);
        if (body.detail === "device_already_consumed_free_intro") {
          setDeviceBlocked(true);
        }
      } else if (status === 403) {
        // Logged-in but somehow blocked — surface the backend detail.
        setError(body.detail || t.error_generic);
      } else if (status === 409) {
        // Pool not ready (admin needs to add questions).
        setError(body.detail || t.error_generic);
      } else {
        // Network / 500 — surface backend detail when present.
        setError(body.detail || t.error_generic);
      }
    } finally {
      setStarting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }

  if (deviceBlocked) {
    return (
      <div data-testid="vip-exam-page">
        <DeviceBlockedView t={t} packs={packs} />
      </div>
    );
  }

  return (
    <div data-testid="vip-exam-page">
      {error && (
        <div className="max-w-4xl mx-auto rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 inline-flex items-center gap-2 text-rose-800"
             data-testid="vip-exam-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}
      <IntroView t={t} language={language} onStart={handleStart} starting={starting}
                 hasInProgress={!!active} history={history}
                 credits={credits} packs={packs}
                 difficulty={difficulty} setDifficulty={setDifficulty}
                 canPickDifficulty={canPickDifficulty}
                 freeMode={!canPickDifficulty} />
    </div>
  );
}

// ===== Single-attempt page (runner OR summary depending on status) =====
export function VIPExamAttempt() {
  const { attemptId } = useParams();
  const navigate = useNavigate();
  // Phase 6.2: the runner is ALWAYS in Italian. Real exam, real language.
  const language = "it";
  const [attempt, setAttempt] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const t = useMemo(() => UI[language] || UI.it, []);

  const reload = async () => {
    setLoading(true);
    try {
      const [res, hist, cr] = await Promise.all([
        apiGet(`/vip/exam/${attemptId}`),
        // Needed for achievement-badge computation (first_pass / triplete).
        apiGet("/vip/exam/attempts").catch(() => ({ attempts: [] })),
        // Needed for the FinalModuleModal "buy more attempts" CTA URL.
        apiGet("/vip/exam/credits").catch(() => null),
      ]);
      const merged = res?.attempt ? { ...res.attempt, _packs: cr?.packs || null } : null;
      setAttempt(merged);
      setHistory(hist?.attempts || []);
    } catch (e) {
      setError(t.error_generic);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { reload(); }, [attemptId]); // eslint-disable-line

  const handleSubmit = async ({ silent = false } = {}) => {
    if (submitting) return;
    setSubmitting(true);
    try {
      const res = await apiPost(`/vip/exam/${attemptId}/submit`);
      setAttempt(res?.attempt || null);
      // Refresh history so the freshly submitted attempt is in there
      // when AchievementBadges computes.
      apiGet("/vip/exam/attempts").then((h) => setHistory(h?.attempts || []))
                                  .catch(() => {});
    } catch (e) {
      if (!silent) setError(t.error_generic);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-[40vh] grid place-items-center">
        <Loader2 className="h-8 w-8 animate-spin text-brand-primary" />
      </div>
    );
  }
  if (!attempt) {
    return (
      <div className="max-w-3xl mx-auto py-12 text-center">
        <p className="text-brand-secondary mb-4">{error || "—"}</p>
        <Link to="/vip/exam" className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-brand-primary text-white">
          <ArrowLeft className="h-4 w-4" /> {t.summary_back_home}
        </Link>
      </div>
    );
  }

  if (attempt.status === "submitted") {
    // Phase 7 — Funnel routing based on attempt.mode.
    // "intro" → motivational + CTA to start preview
    // "preview" → paywall (3 hard questions done)
    // "real" → standard summary
    const mode = attempt.mode || "real";
    if (mode === "intro") {
      return (
        <div data-testid="vip-exam-attempt-page">
          <IntroDoneView
            t={t} locale={language} attempt={attempt}
            starting={submitting}
            onBack={() => navigate("/vip/oral")}
            onStartPreview={async () => {
              if (submitting) return;
              setSubmitting(true);
              try {
                const fingerprint = buildFingerprintComponents();
                const res = await apiPost("/vip/exam/start-preview", { fingerprint });
                const aid = res?.attempt?.attempt_id;
                if (aid) navigate(`/vip/exam/${aid}`);
              } catch (e) { setError(t.error_generic); }
              finally { setSubmitting(false); }
            }}
          />
        </div>
      );
    }
    if (mode === "preview") {
      return (
        <div data-testid="vip-exam-attempt-page">
          <PreviewLockedView t={t} packs={attempt._packs} />
        </div>
      );
    }
    return (
      <div data-testid="vip-exam-attempt-page">
        <SummaryView t={t} locale={language} attempt={attempt} history={history}
                     onNewAttempt={async () => {
                       try {
                         const fingerprint = buildFingerprintComponents();
                         const res = await apiPost("/vip/exam/start", { fingerprint });
                         const aid = res?.attempt?.attempt_id;
                         if (aid) navigate(`/vip/exam/${aid}`);
                       } catch (e) { setError(t.error_generic); }
                     }} />
      </div>
    );
  }

  return (
    <div data-testid="vip-exam-attempt-page">
      {error && (
        <div className="max-w-4xl mx-auto rounded-xl bg-rose-50 border border-rose-200 px-4 py-3 mb-4 inline-flex items-center gap-2 text-rose-800"
             data-testid="vip-exam-attempt-error">
          <AlertCircle className="h-4 w-4" /> {error}
        </div>
      )}
      <RunnerView t={t} locale={language} attempt={attempt}
                  onAttemptUpdate={(a) =>
                    // Preserve `_packs` across child-driven updates so the
                    // FinalModuleModal can keep showing the "buy more"
                    // CTA even after a slot advance refreshes the attempt.
                    setAttempt((prev) => ({ ...a, _packs: prev?._packs || a?._packs }))}
                  onSubmit={handleSubmit}
                  submitting={submitting} />
    </div>
  );
}
